// FILE: src/pages/AccessLinkPage.jsx
// /access/:token — a reviewer opens a private access link (plan WP-2b,
// matrix 10.5): 1) their own email address, 2) the emailed code, their name,
// a password (MTO "Strong Password") and five security questions (1.2.1).
// Then they're signed in like any account. Used, revoked and unknown links
// say so plainly.
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import PublicLayout from "../layouts/PublicLayout";
import PasswordRules from "../components/PasswordRules";
import { LoadingState } from "../components/ui";
import { apiFetch } from "../services/api";
import { claimAccessLink, openAccessLink, startAccessLink } from "../services/accessLinks";
import { errorBody, errorCode, errorMessage } from "../i18n/errors";
import { passwordFailures } from "../utils/passwordPolicy";
import { login } from "../features/authSlice";

const REQUIRED = 5;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function Shell({ children }) {
  return (
    <PublicLayout maxWidth={680}>
      <Card sx={{ borderRadius: 4, my: 2 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>{children}</CardContent>
      </Card>
    </PublicLayout>
  );
}

function Failure({ error }) {
  const left = errorBody(error)?.attemptsRemaining;
  return (
    <Alert severity="error" sx={{ mb: 2 }} role="alert">
      {errorMessage(error)}
      {left != null ? ` ${left} ${left === 1 ? "try" : "tries"} left for this code.` : ""}
    </Alert>
  );
}

export default function AccessLinkPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [link, setLink] = useState(null); // { kind, kindLabel, label }
  const [linkError, setLinkError] = useState(null);
  const [step, setStep] = useState("email"); // email | details
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState("");
  const [bank, setBank] = useState([]);
  const [f, setF] = useState({ code: "", firstName: "", lastName: "", password: "", confirm: "" });
  const [answers, setAnswers] = useState(() => Array.from({ length: REQUIRED }, () => ({ questionId: "", answer: "" })));
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    openAccessLink(token).then(setLink).catch(setLinkError);
  }, [token]);

  useEffect(() => {
    if (step !== "details" || bank.length) return;
    apiFetch("/api/auth/challenge-questions")
      .then((r) => (r.ok ? r.json() : { questions: [] }))
      .then((d) => setBank(d.questions || []))
      .catch(() => setBank([]));
  }, [step, bank.length]);

  const chosen = useMemo(() => new Set(answers.map((a) => a.questionId).filter(Boolean)), [answers]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: k === "code" ? e.target.value.replace(/\D/g, "") : e.target.value }));
  const setAnswer = (i, patch) => setAnswers((prev) => prev.map((a, j) => (j === i ? { ...a, ...patch } : a)));

  async function sendCode(e) {
    e?.preventDefault();
    setError(null);
    setInfo("");
    if (!EMAIL_RE.test(email.trim())) {
      setError({ code: "INVALID_EMAIL" });
      return;
    }
    setBusy(true);
    try {
      await startAccessLink(token, email.trim());
      setStep("details");
      setInfo(`We emailed a 6-digit code to ${email.trim()}. It expires in 10 minutes.`);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  const problems = {
    code: f.code.length !== 6,
    firstName: !f.firstName.trim(),
    lastName: !f.lastName.trim(),
    password: passwordFailures(f.password).length > 0,
    confirm: f.confirm !== f.password,
    answers: answers.some((a) => !a.questionId || a.answer.trim().length < 2)
  };

  async function create(e) {
    e.preventDefault();
    setTouched(true);
    setError(null);
    if (Object.values(problems).some(Boolean)) return;
    setBusy(true);
    try {
      const data = await claimAccessLink(token, {
        email: email.trim(),
        code: f.code,
        firstName: f.firstName.trim(),
        lastName: f.lastName.trim(),
        password: f.password,
        challengeAnswers: answers.map((a) => ({ questionId: a.questionId, answer: a.answer.trim() }))
      });
      // Signed in by the server (session cookie); tell the app who it is.
      dispatch({ type: login.fulfilled.type, payload: data });
      navigate(data.user.role === "admin" ? "/admin" : "/student", { replace: true });
    } catch (err) {
      setError(err);
      const code = errorCode(err);
      if (code === "OTP_EXPIRED") {
        setF((x) => ({ ...x, code: "" }));
        setInfo("");
      }
      if (code === "ACCESS_LINK_USED" || code === "ACCESS_LINK_INVALID") setLinkError(err);
    } finally {
      setBusy(false);
    }
  }

  if (linkError) {
    const used = errorCode(linkError) === "ACCESS_LINK_USED";
    return (
      <Shell>
        <Box aria-hidden sx={{ width: 52, height: 52, borderRadius: 3, display: "grid", placeItems: "center", mb: 2, bgcolor: "zun.errorTint", color: "error.main" }}>
          <LinkOffIcon />
        </Box>
        <Typography variant="h1" sx={{ mb: 1 }}>
          {used ? "This link has already been used" : "This link isn't valid"}
        </Typography>
        <Typography sx={{ color: "text.secondary", mb: 3 }} data-testid="access-link-dead">
          {used
            ? "Each access link works once. If you set up your account with it, sign in with your email and password."
            : "The link may be mistyped or it was turned off. Ask the person who sent it for a new one."}
        </Typography>
        <Button component={RouterLink} to="/login" variant="contained" size="large">
          Go to sign in
        </Button>
      </Shell>
    );
  }
  if (!link) {
    return (
      <Shell>
        <LoadingState variant="inline" label="Checking your link…" />
      </Shell>
    );
  }

  return (
    <Shell>
      <Typography sx={{ color: "text.secondary", fontWeight: 600, mb: 0.5 }}>{link.kindLabel}</Typography>
      <Typography variant="h1" sx={{ mb: 1 }}>
        Set up your account
      </Typography>
      <Typography sx={{ color: "text.secondary", mb: 3 }}>
        {link.label}. This private link works once: use your own email address, which you'll sign in with from now on.
      </Typography>

      {error && <Failure error={error} />}
      {info && (
        <Alert severity="info" sx={{ mb: 2 }} role="status">
          {info}
        </Alert>
      )}

      {step === "email" && (
        <Box component="form" onSubmit={sendCode} noValidate>
          <Stack spacing={2.5}>
            <TextField
              label="Your email address"
              type="email"
              required
              autoComplete="email"
              inputProps={{ inputMode: "email" }}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              helperText="We'll email you a code to confirm it's yours."
            />
            <Button type="submit" variant="contained" size="large" disabled={busy || !email}>
              {busy ? <CircularProgress size={22} color="inherit" aria-label="Sending" /> : "Email me a code"}
            </Button>
          </Stack>
        </Box>
      )}

      {step === "details" && (
        <Box component="form" onSubmit={create} noValidate>
          <Stack spacing={2.5}>
            <TextField
              label="Verification code"
              required
              autoFocus
              autoComplete="one-time-code"
              inputProps={{ inputMode: "numeric", maxLength: 6, pattern: "[0-9]*", style: { letterSpacing: "0.3em", fontWeight: 650 } }}
              value={f.code}
              onChange={set("code")}
              error={touched && problems.code}
              helperText={touched && problems.code ? "Enter the 6 digits from the email." : " "}
            />
            <Stack direction="row" spacing={2} sx={{ alignItems: "center", flexWrap: "wrap" }}>
              <Button onClick={sendCode} disabled={busy}>
                Send a new code
              </Button>
              <Button
                onClick={() => {
                  setStep("email");
                  setInfo("");
                  setError(null);
                }}
                disabled={busy}
              >
                Use a different email
              </Button>
            </Stack>

            <Typography variant="h2" sx={{ fontSize: "1.25rem", pt: 1 }}>
              About you
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField label="First name" required autoComplete="given-name" fullWidth value={f.firstName} onChange={set("firstName")} error={touched && problems.firstName} helperText={touched && problems.firstName ? "Enter your first name." : " "} />
              <TextField label="Last name" required autoComplete="family-name" fullWidth value={f.lastName} onChange={set("lastName")} error={touched && problems.lastName} helperText={touched && problems.lastName ? "Enter your last name." : " "} />
            </Stack>

            <Typography variant="h2" sx={{ fontSize: "1.25rem", pt: 1 }}>
              Password
            </Typography>
            <TextField
              label="New password"
              type="password"
              required
              autoComplete="new-password"
              value={f.password}
              onChange={set("password")}
              error={touched && problems.password}
              helperText={touched && problems.password ? errorMessage("WEAK_PASSWORD") : " "}
              inputProps={{ "aria-describedby": "password-rules" }}
            />
            <PasswordRules password={f.password} />
            <TextField
              label="Confirm password"
              type="password"
              required
              autoComplete="new-password"
              value={f.confirm}
              onChange={set("confirm")}
              error={touched && problems.confirm}
              helperText={touched && problems.confirm ? "The two passwords don't match." : " "}
            />

            <Typography variant="h2" sx={{ fontSize: "1.25rem", pt: 1 }}>
              Five security questions
            </Typography>
            <Typography sx={{ color: "text.secondary", mt: "-8px !important" }}>
              Used to confirm it's you, like for every student. Capitals and extra spaces don't matter.
            </Typography>
            {answers.map((a, i) => (
              <Stack key={i} direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <FormControl fullWidth required error={touched && !a.questionId} sx={{ flex: 3 }}>
                  <InputLabel id={`al-q${i}-label`}>{`Question ${i + 1}`}</InputLabel>
                  <Select labelId={`al-q${i}-label`} label={`Question ${i + 1}`} value={a.questionId} onChange={(e) => setAnswer(i, { questionId: e.target.value })}>
                    {bank.map((q) => (
                      <MenuItem key={q.id} value={q.id} disabled={chosen.has(q.id) && q.id !== a.questionId}>
                        {q.text}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label={`Answer ${i + 1}`}
                  required
                  autoComplete="off"
                  value={a.answer}
                  onChange={(e) => setAnswer(i, { answer: e.target.value })}
                  error={touched && a.answer.trim().length < 2}
                  helperText={touched && a.answer.trim().length < 2 ? "At least 2 characters." : " "}
                  sx={{ flex: 2 }}
                />
              </Stack>
            ))}
            <Button type="submit" variant="contained" size="large" disabled={busy}>
              {busy ? <CircularProgress size={22} color="inherit" aria-label="Creating your account" /> : "Create my account"}
            </Button>
          </Stack>
        </Box>
      )}

      <Typography sx={{ color: "text.secondary", mt: 3 }}>
        Already set up?{" "}
        <Link component={RouterLink} to="/login">
          Sign in
        </Link>
      </Typography>
    </Shell>
  );
}
